import { useState } from 'react';
import { useTransfers, useGuestTransfers } from '@/hooks/useTransfer';
import { Spinner, EmptyState, Pagination } from '@/components/common';
import { cn } from '@/lib/utils';
import type { TransferFilter as TransferFilterType } from '@/types';
import { useAuthStore } from '@/store/authStore';
import { Select, MenuItem } from '@mui/material';
import TransferCard from '@/components/card/TransferCard';
import { TransferFilter } from '@/components/transfer/TransferFilter';

const LIMIT = 12; // Matching job list limit or keeping 9? Let's use 12 for grid.

export default function TransferListPage() {
  const { isAuthenticated, user } = useAuthStore();

  const [filters, setFilters] = useState<TransferFilterType>({
    page: 1,
    limit: LIMIT,
    orderBy: 'createdAt',
    orderDirection: 'DESC',
  });

  const { data, isLoading, isFetching } = useTransfers(filters);

  const handleFilterSearch = (searchFilters: any) => {
    setFilters((prev) => {
      const newFilters = { ...prev, ...searchFilters, page: 1 };
      // Omit undefined/null values
      const cleanFilters = Object.fromEntries(
        Object.entries(newFilters).filter(([_, v]) => v !== undefined && v !== null && v !== '')
      );
      // Ensure page and limit are retained
      return {
        ...cleanFilters,
        page: 1,
        limit: LIMIT,
        orderBy: prev.orderBy,
        orderDirection: prev.orderDirection,
      } as TransferFilterType;
    });
  };

  const items = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="pb-20">
      {/* Header */}
      <div className="mb-6">
        <div className="section-label">Sang nhượng</div>
        <div className="flex items-end justify-between">
          <h1 className="section-title mb-0">Danh sách sang nhượng</h1>
          {total > 0 && (
            <span className="text-sm" style={{ color: 'var(--text-3)' }}>
              {total} kết quả
            </span>
          )}
        </div>
      </div>

      {/* New TopCV-style Filter */}
      <div className="sticky top-[104px] z-30 mb-8">
        <TransferFilter onSearch={handleFilterSearch} initialFilters={filters} />
      </div>

      {/* Sort options (optional, maybe keep it outside or integrate, JobFilter doesn't have sort. Transfer had sort. Let's put a simple sort on the right of the header if needed, or leave it as JobFilter style without sort for now, wait, transfer used to have order options). Let's just keep the header. If user wants to sort they might ask later. Actually, the original had sort: Mới nhất, Giá tăng dần, Giá giảm dần. I will add a small sort dropdown below the filter, aligned right. */}

      <div className="flex justify-end mb-4">
        <Select
          value={`${filters.orderBy}|${filters.orderDirection}`}
          onChange={(e) => {
            const [orderBy, orderDirection] = (e.target.value as string).split('|') as [TransferFilterType['orderBy'], 'ASC' | 'DESC'];
            setFilters(prev => ({ ...prev, orderBy, orderDirection, page: 1 }));
          }}
          size="small"
          sx={{
            minWidth: '160px',
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: '500',
            color: '#374151',
            bgcolor: 'white',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#e5e7eb',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#f97316',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#f97316',
            }
          }}
        >
          <MenuItem value="createdAt|DESC" sx={{ fontSize: '14px' }}>Mới nhất</MenuItem>
          <MenuItem value="price|ASC" sx={{ fontSize: '14px' }}>Giá tăng dần</MenuItem>
          <MenuItem value="price|DESC" sx={{ fontSize: '14px' }}>Giá giảm dần</MenuItem>
          <MenuItem value="revenue|DESC" sx={{ fontSize: '14px' }}>Doanh thu cao nhất</MenuItem>
          <MenuItem value="profit|DESC" sx={{ fontSize: '14px' }}>Lợi nhuận cao nhất</MenuItem>
        </Select>
      </div>

      {/* ── Results ── */}
      {isLoading ? (
        <Spinner />
      ) : !items.length ? (
        <EmptyState title="Không tìm thấy tin sang nhượng" description="Thử thay đổi bộ lọc hoặc tìm kiếm khác" icon="🏪" />
      ) : (
        <>
          <div className={cn('grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 transition-opacity', isFetching && 'opacity-60')}>
            {items.map((item) => (
              <TransferCard key={item.id} item={item} />
            ))}
          </div>

          {/* Pagination */}
          <Pagination page={filters.page ?? 1} totalPages={totalPages} onChange={(p) => setFilters((f) => ({ ...f, page: p }))} />
        </>
      )}
    </div>
  );
}
